import React, { useCallback, useMemo } from 'react';

// DEPENDENCIES
import identity from 'lodash/identity';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useFormikContext } from 'formik';

// HELPERS
import { withRouter } from 'helpers/withRouter';

// GLOBAL VARIABLES
import { INVENTORY_FILE_TYPE_IDS } from 'globals.js';

// CORE COMPONENTS
import Form from 'core/form/Form';

// GLOBAL PARTS
import Card from 'parts/card/Card';

// LOCAL COMPONENTS
import VINStock from '../tools/VINStock';
import Attachment from 'routers/interface/tools/attachment/Attachment';

// LOCAL VARIABLES
import { WARRANTY_OPTION_KEYS, VEHICLE_MAKE_OPTION_KEYS, INVENTORY_LOCATIONS_OPTION_KEYS } from '../../../variables';

// STYLES
import styles from '../../trade.module.scss';

// MAIN COMPONENT
const VehicleInfoForm = compose(
  withRouter,
  connect(({ lookups }) => ({ lookups }))
)(
  ({
    index,
    // REDUX
    lookups: { exteriorColors, interiorColors }
  }) => {

    // CONTEXT
    const { values, setFieldValue, validateField } = useFormikContext() || {};
    const { aryTrade } = useMemo(() => values || {}, [values]);

    // MEMOS
    const locatedAtDealership = useMemo(
      () =>  parseInt(aryTrade?.[index]?.inventory?.located_at_dealership),
      [aryTrade, index]
    )
    const lookupParams = useMemo(
      () => ({
        inventoryLocation: {
          newUsed: 1,
          vehicleAtDealership: locatedAtDealership
        }
      }),
      [locatedAtDealership]
    );

    // CALLBACKS
    const handleLocationChange = useCallback(
      () => {
        setFieldValue(`aryTrade[${index}].inventory.location_type_id`, undefined);
        validateField(`aryTrade[${index}].inventory.location_type_id`);
      },
      [setFieldValue, validateField, index]
    )

    const vehicle = values.aryTrade?.[index]?.inventory;
    const stockNum = vehicle?.stock_num;
    const vinNum = vehicle?.vin_num;
    const dailySalesInventoryId = vehicle?.daily_sales_inventory_id;
    const uploadParams = { stockNum, vinNum, dailySalesInventoryId };
    const noVinStock = !stockNum || !vinNum;
    
    return (
      <Form.Body.Vertical className={styles.body} fullWidth>
        <Form.PlainText label="Type" value="New / DX" />
        <Form.YesNo
          name={`aryTrade[${index}].inventory.located_at_dealership`}
          label="Is the Vehicle Physically At the Dealership?"
          onChange={handleLocationChange}
          numberic
          required
        />
        {(locatedAtDealership === 0 || aryTrade?.[index]?.trade_id) && (
          <Form.Checklist
            vertical
            lookup={locatedAtDealership ? 'inventoryLocationsAtDealerNew' : 'inventoryLocationsNotAtDealerNew'}
            type="radio"
            name={`aryTrade[${index}].inventory.location_type_id`}
            label="Location"
            optionKeys={INVENTORY_LOCATIONS_OPTION_KEYS}
            lookupParams={lookupParams.inventoryLocation}
            optionsMapper={options =>
              options.map(locatedAtDealership ? o => ({
                label: (
                  <>
                    <div className="d-block">{o.label}</div>
                    <div className="small text-gray-500 pt-2">
                      {o.value === 3
                        ? 'NOTE: Payment made within 48 hours.'
                        : o.value === 4
                        ? 'NOTE: This status should only be used when a vehicle checked in physically has later moved off site or has been transported to an auction.'
                        : ''}
                    </div>
                  </>
                ),
                value: o.value
              }) : identity)
            }
            useNone
            required
            text={!locatedAtDealership && (
              <div className="small text-gray-500">
                <p className="mb-2">NOTE: Accounting will Not Process Payment until the vehicle is Physically Checked In.</p>
                <p>An Inventory E-Post will be sent to Accounting when the vehicle has been Physically Checked In.</p>
              </div>
            )}
            list={{
              direction: 'vertical'
            }}
        />
        )}
        {locatedAtDealership === 0 && (
          <>
            <Form.Date
              vertical
              name={`aryTrade[${index}].inventory.location_arrival_date`}
              label="Estimated Date of Arrival for Physical Check-in"
              required
            />
            <Form.Textarea vertical name={`aryTrade[${index}].inventory.location_comments`} label="Location Comments" />
          </>
        )}
        <VINStock index={index} isNew required />
        <Form.Control label="Year" name={`aryTrade[${index}].inventory.vehicle_year`} schema="year" required />
        <Form.Select
          name={`aryTrade[${index}].inventory.make_id`}
          label="Make"
          lookup="vehicleMakes"
          optionKeys={VEHICLE_MAKE_OPTION_KEYS}
          required
          useBlank
        />
        <Form.Control name={`aryTrade[${index}].inventory.model`} label="Model" required />
        <Form.Control name={`aryTrade[${index}].inventory.model_code`} label="Model Code" required />
        <Form.Control name={`aryTrade[${index}].inventory.vehicle_trim`} label="Trim" required />
        <Form.Control name={`aryTrade[${index}].inventory.body_style`} label="Body style" required />
        <Form.Select
          allowCustom
          name={`aryTrade[${index}].inventory.ext_color`}
          label="Exterior"
          options={exteriorColors}
          required
          key={exteriorColors?.length}
        />
        <Form.Select
          allowCustom
          name={`aryTrade[${index}].inventory.int_color`}
          label="Interior"
          options={interiorColors}
          required
          key={interiorColors?.length}
        />
        <Form.Control name={`aryTrade[${index}].inventory.engine`} label="Engine" required />
        <Form.Control name={`aryTrade[${index}].inventory.engine_type`} label="Engine Type" required />
        <Form.Control name={`aryTrade[${index}].inventory.transmission_name`} label="Transmission" required />
        <Form.Control name={`aryTrade[${index}].inventory.drivetrain_name`} label="Drivetrain" required />
        <Form.Control name={`aryTrade[${index}].inventory.mileage`} label="Mileage" schema="miles" required />
        <Attachment.Upload
          label="Photo Proof of Mileage"
          name={`aryTrade[${index}].inventory.documentAttachment.mileage_photo`}
          uploadType="inventory"
          documentType={INVENTORY_FILE_TYPE_IDS.MILEAGE_PHOTO_NEW}
          docrName={`aryTrade[${index}].inventory.docrAttachmentRequest`}
          requestedFor="Inventory"
          uploadParams={uploadParams}
          noVinStock={noVinStock}
          vehicle={vehicle}
        />
        <Form.Select
          name={`aryTrade[${index}].inventory.warranty_type`}
          label="Warranty type"
          lookup="warrantyTypes"
          optionKeys={WARRANTY_OPTION_KEYS}
          useBlank
        />
      </Form.Body.Vertical>
    );
  }
);

// EXPORT
export default function VehicleInfo(props) {
  return <Card body={<VehicleInfoForm {...props} />} />;
}
